import { HttpClient } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { environment } from '@environments/environment';
import { ApplicationResponse } from '@models/api-calls/application/application-response.model';
import { ApplicationInformationSubSteps, MainSteps, PersonalDetailsSubSteps } from '@models/config/app-structure.model';
import { ApplicationService } from '@services/api-calls/application.service';
import { EdeliveryService } from '@services/conditions/edelivery.service';
import { ErrorHandlingService } from '@services/error-handling.service';
import { FormRegistryService } from '@services/form/form-registry.service';
import {
    ContactInformationFormEnum,
    ExistingInsuranceSubstepFormEnum,
    StatementsAndAgreementsFormEnum,
    YesNoOptionsEnum,
} from 'app/enums/form/form.enum';
import { catchError, Observable, tap } from 'rxjs';

const API_URL = environment.apiUrl;

@Injectable({
    providedIn: 'root',
})
export class ApplicationDataService {
    applicationSaveInProgress = signal<boolean>(false);

    constructor(
        private http: HttpClient,
        private formRegistry: FormRegistryService,
        private applicationService: ApplicationService,
        private errorHandlingService: ErrorHandlingService,
        private edeliveryService: EdeliveryService,
    ) {}

    updateApplication(options?: { silent?: boolean }): Observable<ApplicationResponse> | undefined {
        const currentApplication = this.applicationService.rawApplication();
        const applicationId = currentApplication?.id;

        if (!applicationId || !currentApplication) {
            return undefined;
        }
        const modifiedData = this.createModifiedApplicationData(currentApplication);

        if (!modifiedData) {
            return undefined;
        }

        if (!Object.keys(modifiedData).length || (modifiedData.app_emc && !Object.keys(modifiedData.app_emc).length)) {
            return undefined;
        }

        this.applicationSaveInProgress.set(true);
        const silent = !!options?.silent;

        return this.http.patch<ApplicationResponse>(`${API_URL}/applications/${applicationId}`, modifiedData).pipe(
            tap((response: ApplicationResponse) => {
                this.applicationSaveInProgress.set(false);
                if (!silent && response && response.id) {
                    this.applicationService.refreshApplicationData.update((n) => n + 1);
                }
            }),
            catchError((error) => {
                this.applicationSaveInProgress.set(false);
                return this.errorHandlingService.handleSaveError(error);
            }),
        );
    }

    hasChanges(): boolean {
        const currentApp = this.applicationService.rawApplication();
        if (!currentApp) return false;
        const modified = this.createModifiedApplicationData(currentApp);
        if (!modified) return false;

        const currentAppEmc = currentApp.app_emc;
        const realChanges = Object.keys(modified.app_emc ?? {}).filter((key) => {
            const serverValue = currentAppEmc?.[key as keyof typeof currentAppEmc];
            return serverValue != null;
        });
        return realChanges.length > 0;
    }

    updateHasExistingInsurances(value: number, options?: { silent?: boolean }) {
        return this.patchAppEmcField('has_existing_insurances', value, options);
    }

    updateExistingInsurancesApplied(value: number, options?: { silent?: boolean }) {
        return this.patchAppEmcField('existing_insurances_applied', value, options);
    }

    updateHasStatementsAndAgreements(value: number, options?: { silent?: boolean }) {
        return this.patchAppEmcField('has_statements_and_agreements', value, options);
    }

    updateHasFraudNotice(value: number, options?: { silent?: boolean }) {
        return this.patchAppEmcField('has_fraud_notice', value, options);
    }

    updateMedicalConsent(value: number, options?: { silent?: boolean }) {
        return this.patchAppEmcField('medical_consent', value, options);
    }

    updateEdeliveryConsent(value: number, options?: { silent?: boolean }) {
        return this.patchAppEmcFields(
            {
                emc_edelivery_consent: value,
                emc_edelivery_consent_version: this.edeliveryService.consentVersion(),
            },
            options,
        );
    }

    private patchAppEmcField(field: string, value: number, options?: { silent?: boolean }): Observable<ApplicationResponse> | undefined {
        return this.patchAppEmcFields({ [field]: value }, options);
    }

    private patchAppEmcFields(
        fields: Record<string, number | string | null>,
        options?: { silent?: boolean },
    ): Observable<ApplicationResponse> | undefined {
        const applicationId = this.applicationService.rawApplication()?.id;
        if (!applicationId) return undefined;

        const obs = this.http.patch<ApplicationResponse>(`${API_URL}/applications/${applicationId}`, {
            id: applicationId,
            app_emc: fields,
        });

        if (options?.silent) {
            return obs.pipe(catchError((error) => this.errorHandlingService.handleSaveError(error)));
        }

        return obs.pipe(
            tap((response: ApplicationResponse) => {
                if (response?.id) {
                    this.applicationService.refreshApplicationData.update((n) => n + 1);
                }
            }),
            catchError((error) => this.errorHandlingService.handleSaveError(error)),
        );
    }

    private createModifiedApplicationData(
        currentApplication: ApplicationResponse,
    ): { id: number; app_emc: Record<string, number | string | null> } | null {
        const currentAppEmc = currentApplication.app_emc;

        // Build app_emc data from form controls
        // Use getSafeControlValue (not getValidatedControlValue) for Yes/No question controls
        // because validators like requiredNumericTrue make them invalid when NO (0) is selected,
        // but we still need to save the 0 value to the backend
        const appEmcFormData: Record<string, number | string | null> = {
            has_existing_insurances:
                this.formRegistry.getSafeControlValue<number>([
                    MainSteps.APPLICATION_INFORMATION,
                    ApplicationInformationSubSteps.EXISTING_INSURANCE,
                    ExistingInsuranceSubstepFormEnum.HAS_EXISTING_INSURANCES,
                ]) ??
                currentAppEmc?.has_existing_insurances ??
                null,
            existing_insurances_applied:
                this.formRegistry.getSafeControlValue<number>([
                    MainSteps.APPLICATION_INFORMATION,
                    ApplicationInformationSubSteps.EXISTING_INSURANCE_REPLACEMENT,
                    ExistingInsuranceSubstepFormEnum.EXISTING_INSURANCES_APPLIED,
                ]) ??
                currentAppEmc?.existing_insurances_applied ??
                null,
            has_statements_and_agreements:
                this.formRegistry.getSafeControlValue<number>([
                    MainSteps.APPLICATION_INFORMATION,
                    ApplicationInformationSubSteps.STATEMENTS_AND_AGREEMENTS,
                    StatementsAndAgreementsFormEnum.STATEMENTS_AND_AGREEMENTS_DISCLOSURE,
                ]) ??
                currentAppEmc?.has_statements_and_agreements ??
                null,
            has_fraud_notice:
                this.formRegistry.getSafeControlValue<number>([
                    MainSteps.APPLICATION_INFORMATION,
                    ApplicationInformationSubSteps.STATEMENTS_AND_AGREEMENTS,
                    StatementsAndAgreementsFormEnum.FRAUD_NOTICE_DISCLOSURE,
                ]) ??
                currentAppEmc?.has_fraud_notice ??
                null,
            emc_edelivery_opt_in:
                this.formRegistry.getSafeControlValue<number>([
                    MainSteps.PERSONAL_DETAILS,
                    PersonalDetailsSubSteps.CONTACT_INFORMATION,
                    ContactInformationFormEnum.EDELIVERY_OPT_IN,
                ]) ??
                currentAppEmc?.emc_edelivery_opt_in ??
                null,
            emc_edelivery_consent:
                this.formRegistry.getSafeControlValue<number>([
                    MainSteps.APPLICATION_INFORMATION,
                    ApplicationInformationSubSteps.STATEMENTS_AND_AGREEMENTS,
                    StatementsAndAgreementsFormEnum.EDELIVERY_CONSENT,
                ]) ??
                currentAppEmc?.emc_edelivery_consent ??
                null,
        };

        // Compare with current server values
        const changedAppEmcValues = Object.entries(appEmcFormData).reduce(
            (accumulator, [key, value]) => {
                const serverValue = currentAppEmc?.[key as keyof typeof currentAppEmc];
                if (serverValue !== value) {
                    (accumulator as Record<string, unknown>)[key] = value;
                }
                return accumulator;
            },
            {} as Record<string, number | string | null>,
        );

        // Reset existing_insurances_applied when has_existing_insurances is set to No
        const hasExistingInsurances = changedAppEmcValues['has_existing_insurances'] ?? appEmcFormData['has_existing_insurances'];
        if (hasExistingInsurances === 0 && currentAppEmc?.existing_insurances_applied !== null) {
            changedAppEmcValues['existing_insurances_applied'] = null;
        }

        // When the user opted out (or the feature is unavailable) never persist a consent
        // answer: drop any residual value and reset a previously saved one to null
        const edeliveryOptIn = changedAppEmcValues['emc_edelivery_opt_in'] ?? appEmcFormData['emc_edelivery_opt_in'];
        const edeliveryConsentRevoked = edeliveryOptIn !== YesNoOptionsEnum.YES || !this.edeliveryService.isFeatureAvailable();
        if (edeliveryConsentRevoked) {
            delete changedAppEmcValues['emc_edelivery_consent'];
            delete changedAppEmcValues['emc_edelivery_consent_version'];
            if (currentAppEmc?.emc_edelivery_consent != null) {
                changedAppEmcValues['emc_edelivery_consent'] = null;
                changedAppEmcValues['emc_edelivery_consent_version'] = null;
            }
        }

        // The opt-in itself is only persisted while the feature is available
        if (!this.edeliveryService.isFeatureAvailable()) {
            delete changedAppEmcValues['emc_edelivery_opt_in'];
        }

        // Every persisted consent answer carries the consent-text version it was given for
        const edeliveryConsent = changedAppEmcValues['emc_edelivery_consent'];
        if (edeliveryConsent === YesNoOptionsEnum.YES || edeliveryConsent === YesNoOptionsEnum.NO) {
            changedAppEmcValues['emc_edelivery_consent_version'] = this.edeliveryService.consentVersion();
        }

        // If no changes, return null
        if (Object.keys(changedAppEmcValues).length === 0) {
            return null;
        }

        // Build final payload
        return {
            id: currentApplication.id,
            app_emc: changedAppEmcValues,
        };
    }
}
